import { gsap } from 'gsap';
import { loadSwiperModule } from '../../core/base/swiper-loader.js';






function getCurrentLang() {
  return document.body.classList.contains('set-lang-en') ? 'en' : 'de';
}


function getLocalizedDataAttr(el, baseName) {
  if (!el) return '';
  const lang = getCurrentLang();
  const valLang = el.getAttribute(`${baseName}-${lang}`);
  if (valLang && valLang.trim()) return valLang.trim();

  
  const valBase = el.getAttribute(baseName);
  if (valBase && valBase.trim()) return valBase.trim();

  return '';
}




const HERO_VISITENKARTE_STYLE_MAP = Object.freeze({
  default: '',
  base: '',
  original: '',
  none: '',
  webdesign: 'hero-viscard-webdesign',
  ai: 'hero-viscard-ai',
  'hero-viscard-webdesign': 'hero-viscard-webdesign',
  'hero-viscard-ai': 'hero-viscard-ai',
});

const HERO_VISITENKARTE_STYLE_LABELS = Object.freeze({
  '': 'default',
  'hero-viscard-webdesign': 'webdesign',
  'hero-viscard-ai': 'ai',
});

function normalizeHeroVisitenkarteStyle(style) {
  const raw = String(style ?? 'default').trim().toLowerCase();
  if (raw in HERO_VISITENKARTE_STYLE_MAP) return HERO_VISITENKARTE_STYLE_MAP[raw];
  return null;
}

function getHeroVisitenkartenWrappers(root = document) {
  if (!root || !root.querySelectorAll) return [];
  return [...root.querySelectorAll('.hero-visitenkarten-wrapper')];
}

function readActiveHeroVisitenkarteStyle(root = document) {
  const wrappers = getHeroVisitenkartenWrappers(root);
  if (!wrappers.length) {
    return {
      ok: false,
      reason: 'no-hero-visitenkarte-wrapper',
      style: 'default',
      key: null,
      wrappers: 0,
      available: ['default', 'webdesign', 'ai'],
    };
  }
  const rawKey = (wrappers[0].getAttribute('data-active-key') || '').trim();
  const style = HERO_VISITENKARTE_STYLE_LABELS[rawKey] || (rawKey || 'default');
  return {
    ok: true,
    style,
    key: rawKey || null,
    wrappers: wrappers.length,
    available: ['default', 'webdesign', 'ai'],
  };
}

function setHeroVisitenkarteStyle(style = 'default', root = document) {
  const resolvedKey = normalizeHeroVisitenkarteStyle(style);
  if (resolvedKey === null) {
    return {
      ok: false,
      reason: 'unknown-style',
      style: String(style ?? ''),
      key: null,
      wrappers: getHeroVisitenkartenWrappers(root).length,
      available: ['default', 'webdesign', 'ai'],
    };
  }

  const wrappers = getHeroVisitenkartenWrappers(root);
  wrappers.forEach((wrap) => {
    if (resolvedKey) wrap.setAttribute('data-active-key', resolvedKey);
    else wrap.removeAttribute('data-active-key');
  });

  const styleName = HERO_VISITENKARTE_STYLE_LABELS[resolvedKey] || 'default';
  return {
    ok: wrappers.length > 0,
    style: styleName,
    key: resolvedKey || null,
    wrappers: wrappers.length,
    available: ['default', 'webdesign', 'ai'],
  };
}

function registerHeroVisitenkarteConsoleApi() {
  if (typeof window === 'undefined') return;
  window.setHeroVisitenkarteStyle = (style = 'default') => setHeroVisitenkarteStyle(style);
  window.getHeroVisitenkarteStyle = () => readActiveHeroVisitenkarteStyle();
  window.listHeroVisitenkarteStyles = () => ['default', 'webdesign', 'ai'];
  window.vkStyle = window.setHeroVisitenkarteStyle;
}

function setTooltipTextFade(fixedTip, text, duration) {
  if (!fixedTip) return;
  if (fixedTip.dataset.tipMode === 'brand-stagger') {
    setTooltipTextStagger(fixedTip, text, duration);
    return;
  }

  const resolveDuration = () => {
    if (Number.isFinite(duration)) return duration;
    const cs = getComputedStyle(fixedTip);
    const raw = cs.getPropertyValue('--tip-anim-duration').trim();
    const parsed = raw.endsWith('ms') ? parseFloat(raw) : (raw.endsWith('s') ? parseFloat(raw) * 1000 : parseFloat(raw));
    return Number.isFinite(parsed) ? parsed : 160;
  };
  const dur = resolveDuration();

  if (!fixedTip._fadeTransitionInit) {
    const cs = getComputedStyle(fixedTip);
    const hasTransition = cs.transitionProperty && cs.transitionProperty.includes('opacity');
    if (!hasTransition) {
      fixedTip.style.transition = `opacity ${dur}ms ease`;
    }
    fixedTip._fadeTransitionInit = true;
  }

  if (fixedTip._fadeTimers) fixedTip._fadeTimers.forEach(t => clearTimeout(t));
  fixedTip._fadeTimers = [];

  const next = text ?? '';
  if (fixedTip.textContent === next) return;

  fixedTip.classList?.add?.('has-tooltip-hover');

  fixedTip.offsetHeight; 
  fixedTip.style.opacity = '0';

  fixedTip._fadeTimers.push(setTimeout(() => {
    fixedTip.textContent = next;
    fixedTip.offsetHeight; 
    fixedTip.style.opacity = '';
    fixedTip._fadeTimers = [];
  }, dur + 20));
}


function setTooltipTextStagger(target, text, duration) {
  if (!target) return;

  const nextRaw = String(text ?? '').trim();
  if ((target.dataset.currentTooltipText || '') === nextRaw) return;
  target.dataset.currentTooltipText = nextRaw;

  const tokenizeAccent = (raw) => {
    const out = [];
    const re = /(\*[^*]+\*|[^\s]+)/g;
    const parts = raw.match(re) || [];
    parts.forEach((part) => {
      const isAccent = part.startsWith('*') && part.endsWith('*') && part.length >= 2;
      const clean = isAccent ? part.slice(1, -1).trim() : part.trim();
      if (!clean) return;
      clean.split(/\s+/).forEach((word) => {
        if (!word) return;
        out.push({ text: word, accent: isAccent });
      });
    });
    return out;
  };

  const nextTokens = tokenizeAccent(nextRaw);
  const plainText = nextTokens.map((t) => t.text).join(' ').trim();
  target.setAttribute('aria-label', plainText);
  target.classList.add('has-dynamic-tip');

  const runFallback = () => {
    target.textContent = plainText;
  };

  const splitIntoTwoLines = (tokens) => {
    if (!tokens.length) return [[], []];
    if (tokens.length === 1) return [tokens, []];

    const lineLen = (arr) => arr.map((t) => t.text).join(' ').length;
    let bestIdx = -1;
    let bestDiff = Number.POSITIVE_INFINITY;

    for (let i = 1; i < tokens.length; i += 1) {
      const top = tokens.slice(0, i);
      const bottom = tokens.slice(i);
      const topLen = lineLen(top);
      const bottomLen = lineLen(bottom);
      if (bottomLen <= topLen) continue;
      const diff = bottomLen - topLen;
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }

    if (bestIdx === -1) bestIdx = Math.max(1, Math.floor(tokens.length / 2));
    const top = tokens.slice(0, bestIdx);
    const bottom = tokens.slice(bestIdx);
    if (!bottom.length && top.length > 1) {
      return [top.slice(0, -1), [top[top.length - 1]]];
    }
    return [top, bottom];
  };

  if (!gsap || !nextTokens.length) {
    runFallback();
    return;
  }

  const [topWords, bottomWords] = splitIntoTwoLines(nextTokens);
  const currentWords = [...target.querySelectorAll('.vis-tip-word')];
  const incomingGroup = document.createElement('span');
  incomingGroup.className = 'vis-tip-group';

  const createLine = (words, cls) => {
    const line = document.createElement('span');
    line.className = `vis-tip-line ${cls}`;
    words.forEach((word, i) => {
      const part = document.createElement('span');
      part.className = `vis-tip-word${word.accent ? ' vis-tip-word--accent' : ''}`;
      part.textContent = word.text;
      line.appendChild(part);
      if (i < words.length - 1) line.appendChild(document.createTextNode(' '));
    });
    return line;
  };

  incomingGroup.appendChild(createLine(topWords, 'is-top'));
  incomingGroup.appendChild(createLine(bottomWords, 'is-bottom'));
  target.appendChild(incomingGroup);

  const incomingWords = [...incomingGroup.querySelectorAll('.vis-tip-word')];
  const baseDur = Number.isFinite(duration) ? duration / 1000 : 0.36;
  const inDur = Math.max(0.24, baseDur);
  const outDur = Math.max(0.16, baseDur * 0.65);

  const cleanupOldLines = () => {
    [...target.querySelectorAll('.vis-tip-group')].forEach((group) => {
      if (group !== incomingGroup) group.remove();
    });
  };

  gsap.killTweensOf(currentWords);
  gsap.killTweensOf(incomingWords);

  if (currentWords.length) {
    gsap.to(currentWords, {
      opacity: 0,
      y: -6,
      duration: outDur,
      ease: 'power2.out',
      stagger: 0.015,
      overwrite: true,
      onComplete: cleanupOldLines,
    });
  } else {
    cleanupOldLines();
  }

  gsap.set(incomingWords, { opacity: 0, y: 8 });
  gsap.to(incomingWords, {
    opacity: 1,
    y: 0,
    duration: inDur,
    ease: 'power2.out',
    stagger: 0.03,
    overwrite: true,
  });
}


const resolveTooltipText = (item) => {
  if (!item) return '';

  
  const fromData = getLocalizedDataAttr(item, 'data-vis-tooltip');
  if (fromData) return fromData;

  
  const legacy = item.querySelector('.vis-tooltip');
  if (legacy) {
    const lang = getCurrentLang();
    const span = legacy.querySelector(`.lang-${lang}`) || legacy.querySelector('p') || legacy;
    const txt = (span.textContent || '').trim();
    if (txt) return txt;
  }

  return '';
};

function initDbpIntroOpenerImage(root = document) {
  const section = root.querySelector?.('#section-dbp-intro');
  if (!section || section.dataset.dbpOpenerImageInitialized === 'true') return;

  const content = section.querySelector('.dbp-opener-content');
  const media = section.querySelector('.dbp-opener-img-area');
  if (!content || !media) return;

  section.dataset.dbpOpenerImageInitialized = 'true';

  let raf = 0;
  let lastHeight = 0;

  const measure = () => {
    raf = 0;
    const previousDisplay = media.style.display;
    media.style.display = 'none';
    const nextHeight = content.getBoundingClientRect().height;
    media.style.display = previousDisplay;

    if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
    if (Math.abs(nextHeight - lastHeight) <= 0.5) return;

    lastHeight = nextHeight;
    content.style.setProperty('--dbp-opener-img-size', `${nextHeight.toFixed(2)}px`);
  };

  const schedule = () => {
    if (raf) return;
    raf = window.requestAnimationFrame(measure);
  };

  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
  const text = content.querySelector('.dbp-opener-text-area');
  if (observer && text) observer.observe(text);

  const bodyObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
  if (bodyObserver && document.body) bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true, passive: true });
  if (document.fonts?.ready) {
    document.fonts.ready.then(schedule).catch(() => {});
  }

  schedule();
}

export function initVisitenkarten(root = document) {
  initDbpIntroOpenerImage(root);
  if (typeof window !== 'undefined' && !window.__visitenkarteConsoleApiRegistered__) {
    registerHeroVisitenkarteConsoleApi();
    window.__visitenkarteConsoleApiRegistered__ = true;
  }
  const areas = [...root.querySelectorAll('.visitenkarte-area')];
  if (!areas.length) return;

  areas.forEach((area) => {
    if (area.dataset.vcInitialized === 'true') return;
    area.dataset.vcInitialized = 'true';
    const areaRuntimeStartCallbacks = [];
    const areaRuntimeStopCallbacks = [];
    let areaRuntimeActive = false;

    const registerAreaRuntime = (onStart, onStop) => {
      if (typeof onStart === 'function') areaRuntimeStartCallbacks.push(onStart);
      if (typeof onStop === 'function') areaRuntimeStopCallbacks.push(onStop);
    };

    const setAreaRuntimeActive = (next) => {
      const enabled = !!next;
      if (areaRuntimeActive === enabled) return;
      areaRuntimeActive = enabled;
      area.classList.toggle('vc-vp-active', enabled);
      const callbacks = enabled ? areaRuntimeStartCallbacks : areaRuntimeStopCallbacks;
      callbacks.forEach((cb) => {
        try { cb(); } catch {}
      });
    };

    const isAreaRuntimeActive = () => areaRuntimeActive;

    const maxTilt      = parseFloat(area.dataset.vcTilt ?? 20);
    const dampen       = parseFloat(area.dataset.vcDampen ?? 0.3);
    const isHeroArea   = area.classList.contains('hero-visitenkarte-area') || area.id === 'section-hero' || area.classList.contains('section-hero');
    const useParticles = area.dataset.vcParticles === 'true' && !isHeroArea;

    
    let particleLayer = null;
    if (useParticles) {
      particleLayer =
        area.querySelector('.particle-layer') ||
        area.closest('section, .section-hero, .section-dbp')?.querySelector('.particle-layer') ||
        null;

      if (!particleLayer) {
        particleLayer = document.createElement('div');
        particleLayer.className = 'particle-layer';
        area.appendChild(particleLayer);
      }
    }

    
    const wrappers = [...area.querySelectorAll('.visitenkarten-wrapper')];
    if (!wrappers.length) return;

    wrappers.forEach((wrap) => {
      const card       = wrap.querySelector('.visitenkarte');
      const overlay    = wrap.querySelector('.glanz-overlay');
      const reflexWrap = wrap.querySelector('.karte-reflex-wrapper');
      if (!card || !overlay || !reflexWrap) return;
      const hasIconItems = card.querySelectorAll('.icon-item').length > 0;

      
      reflexWrap.querySelector('.visitenkarte-reflex')?.remove();
      const reflexClone = card.cloneNode(true);
      reflexClone.classList.add('visitenkarte-reflex');
      reflexClone.setAttribute('aria-hidden', 'true');
      reflexClone.querySelectorAll('[id]').forEach((el) => {
        el.removeAttribute('id');
      });
      reflexWrap.appendChild(reflexClone);
      reflexClone.querySelectorAll('[role="tablist"]').forEach((item) => {
        item.removeAttribute('role');
        item.removeAttribute('aria-label');
      });
      reflexClone.querySelectorAll('[role="tab"]').forEach((item) => {
        item.removeAttribute('role');
        item.removeAttribute('aria-controls');
        item.removeAttribute('aria-selected');
        item.removeAttribute('tabindex');
      });
      reflexClone.querySelectorAll('.icon-item').forEach((item) => {
        item.removeAttribute('id');
        item.removeAttribute('role');
        item.removeAttribute('aria-controls');
        item.removeAttribute('aria-selected');
        item.removeAttribute('tabindex');
        item.setAttribute('aria-hidden', 'true');
        item.classList.remove('dim', 'active', 'selected', 'selected-dim', 'visitenkarte-element-is-active');
        item.style.pointerEvents = 'none';
      });

      
      const tipParent = card.querySelector('.visitenkarten-inhalt') || wrap;
      const heroBrandTarget = area.classList.contains('hero-visitenkarte-area')
        ? card.querySelector('.visitenkarte-brand-textwrap')
        : null;
      let fixedTip = heroBrandTarget || wrap.querySelector('.vis-fixed-tooltip') || tipParent.querySelector('.vis-fixed-tooltip');
      if (!fixedTip) {
        fixedTip = document.createElement('div');
        fixedTip.className = 'vis-fixed-tooltip';
        fixedTip.setAttribute('aria-live', 'polite');
        tipParent.appendChild(fixedTip);
      } else if (fixedTip.parentElement !== tipParent) {
        if (!heroBrandTarget) tipParent.appendChild(fixedTip);
      }
      if (heroBrandTarget) {
        if (hasIconItems) {
          fixedTip.classList.add('hero-brand-tooltip-target');
          fixedTip.dataset.tipMode = 'brand-stagger';
          fixedTip.setAttribute('aria-live', 'polite');
        } else {
          fixedTip.classList.remove('hero-brand-tooltip-target', 'has-dynamic-tip');
          delete fixedTip.dataset.tipMode;
          delete fixedTip.dataset.currentTooltipText;
          fixedTip.querySelectorAll('.vis-tip-group').forEach((group) => group.remove());
        }
      }

      
      const refreshCurrentTooltip = () => {
        if (!hasIconItems) return;
        
        const selected = card.querySelector('.icon-item.selected') || null;
        
        const txt = selected ? resolveTooltipText(selected) : (fixedTip.textContent || '');
        if (txt) {
          setTooltipTextFade(fixedTip, txt, 100);
          wrap.classList.add('has-tooltip-hover');
        }
      };
      const langObserver = new MutationObserver((muts) => {
        for (const m of muts) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            refreshCurrentTooltip();
            break;
          }
        }
      });
      langObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      
      const onMove = (e) => {
        if (!isAreaRuntimeActive()) return;
        const rect    = wrap.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top  + rect.height / 2;

        const deltaX  = e.clientX - centerX;
        const deltaY  = e.clientY - centerY;

        const rangeX  = (rect.width  / 2) / (parseFloat(area.dataset.vcDampen ?? 0.3) || 0.3);
        const rangeY  = (rect.height / 2) / (parseFloat(area.dataset.vcDampen ?? 0.3) || 0.3);

        const xPercent = Math.max(-1, Math.min(1, deltaX / rangeX));
        const yPercent = Math.max(-1, Math.min(1, deltaY / rangeY));

        const tiltY =  xPercent * (parseFloat(area.dataset.vcTilt ?? 20) || 20);
        const tiltX = -yPercent * (parseFloat(area.dataset.vcTilt ?? 20) || 20);

        card.style.transform        = `rotateX(${tiltX}deg) rotateY(${tiltY}deg) rotateZ(0deg)`;
        reflexClone.style.transform = `rotateX(${tiltX}deg) rotateY(${tiltY}deg) rotateZ(0deg)`;

        const xCard = Math.max(0, Math.min(rect.width,  e.clientX - rect.left));
        const yCard = Math.max(0, Math.min(rect.height, e.clientY  - rect.top));
        overlay.style.setProperty('--x', `${xCard}px`);
        overlay.style.setProperty('--y', `${yCard}px`);

        if (useParticles && particleLayer) {
          const areaRect = area.getBoundingClientRect();
          createParticle(particleLayer, e.clientX - areaRect.left, e.clientY - areaRect.top);
        }
      };

      const onLeave = () => {
        if (!isAreaRuntimeActive()) return;
        card.style.transform        = 'rotateX(0deg) rotateY(0deg) rotateZ(0deg)';
        reflexClone.style.transform = 'rotateX(0deg) rotateY(0deg) rotateZ(0deg)';
      };

      area.addEventListener('mousemove', onMove, { passive: true });
      area.addEventListener('mouseleave', onLeave);

      
      const icons = [...card.querySelectorAll('.icon-item')];

      
      const textContentOf = (el) => (el ? (el.textContent || '').trim() : '');

      const setDimSiblings = (item, on) => {
        const row = item?.parentElement;
        if (!row) return;
        row.querySelectorAll('.icon-item').forEach((sib) => {
          if (sib === item) {
            sib.classList.remove('dim', 'selected-dim');
          } else {
            if (sib.classList.contains('selected')) {
              sib.classList.toggle('selected-dim', !!on);
              sib.classList.remove('dim');
            } else {
              sib.classList.toggle('dim', !!on);
              sib.classList.remove('selected-dim');
            }
          }
        });
      };

      const clearDimInCard = () => {
        card.querySelectorAll('.icon-item.dim, .icon-item.selected-dim')
          .forEach((el) => el.classList.remove('dim', 'selected-dim'));
      };

      registerAreaRuntime(null, () => {
        clearTimeout(wrap._visTipHideTimer);
        clearTimeout(wrap._visTipShowTimer);
        if (!area.classList.contains('dbp-visitenkarte-area')) clearDimInCard();
        wrap.classList.remove('has-tooltip-hover');
      });

      const showFixedTipForItem = (item) => {
        if (!fixedTip) return;
        const txt = resolveTooltipText(item);
        if (!txt) return;
        clearTimeout(wrap._visTipHideTimer);
        clearTimeout(wrap._visTipShowTimer);
        const delay = Math.max(0, parseInt(item.getAttribute('data-delay') || '0', 10));
        wrap._visTipShowTimer = setTimeout(() => {
          setTooltipTextFade(fixedTip, txt);
          wrap.classList.add('has-tooltip-hover');
        }, delay);
        setDimSiblings(item, true);
      };

      const hideFixedTip = () => {
        if (!fixedTip) return;
        clearTimeout(wrap._visTipHideTimer);
        clearTimeout(wrap._visTipShowTimer);
        wrap._visTipHideTimer = setTimeout(() => {
          wrap.classList.remove('has-tooltip-hover');
          clearDimInCard();
        }, 40);
      };

      
      const THEME_VARS_TO_MERGE = [
        '--card-bg',
        '--card-shadow',
        '--tip-maxw',
      ];

      function signalActiveToWrapper(iconEl) {
        const idx0 = icons.indexOf(iconEl);
        const idx1 = idx0 >= 0 ? idx0 + 1 : 0;
        const slug = iconEl?.dataset?.key || (idx1 ? `i${idx1}` : '');

        if (idx1) wrap.setAttribute('data-active', String(idx1));
        else wrap.removeAttribute('data-active');

        if (slug) wrap.setAttribute('data-active-key', slug);
        else wrap.removeAttribute('data-active-key');
      }

      function applyThemeFromIcon(iconEl) {
        if (!iconEl) return;
        const cs = getComputedStyle(iconEl);
        THEME_VARS_TO_MERGE.forEach(v => {
          const val = cs.getPropertyValue(v).trim();
          if (val) {
            wrap.style.setProperty(v, val);
          } else {
            wrap.style.removeProperty(v);
          }
        });
      }

      
      const setupHeroSelection = () => {
        if (!icons.length) return;

        
        const readMs = (str, defMs) => {
          if (str == null) return defMs;
          const s = String(str).trim().toLowerCase();
          if (!s) return defMs;
          if (s.endsWith('ms')) return parseFloat(s);
          if (s.endsWith('s')) return parseFloat(s) * 1000;
          const n = parseFloat(s);
          return Number.isFinite(n) ? n : defMs;
        };
        const getVar = (name) => getComputedStyle(wrap).getPropertyValue(name).trim();

        const startDelay     = readMs(wrap.dataset.heroStartDelay ?? getVar('--hero-autostart-delay'), 3000);
        const rotateInterval = readMs(wrap.dataset.heroInterval ?? getVar('--hero-rotate-interval'), 4000);
        const resumeExtra    = readMs(wrap.dataset.heroResumeExtra ?? getVar('--hero-resume-extra-delay'), 1000);

        
        const getPlaceholder = () => {
          const lang = getCurrentLang();
          const dsVal = lang === 'en'
            ? (wrap.dataset.heroPlaceholderEn || wrap.dataset.heroPlaceholder)
            : (wrap.dataset.heroPlaceholder || wrap.dataset.heroPlaceholderEn);
          if (dsVal && String(dsVal).trim()) return String(dsVal).trim();

          const varVal = getVar(lang === 'en' ? '--hero-placeholder-text-en' : '--hero-placeholder-text');
          if (varVal) return varVal;

          return lang === 'en' ? 'Your new digital calling card' : 'Ihre neue digitale Visitenkarte';
        };

        let placeholderText = getPlaceholder();
        let heroThemeFadeTimer = null;
        let lastActiveKey = '';
        const syncHeroIconAriaLabels = () => {
          icons.forEach((el) => {
            const label = resolveTooltipText(el);
            if (label) el.setAttribute('aria-label', label);
            else el.removeAttribute('aria-label');
          });
        };

        
        let selectedIcon = null;
        icons.forEach((el) => {
          el.classList.remove('selected', 'visitenkarte-element-is-active', 'dim', 'selected-dim');
          el.setAttribute('role', 'button');
          el.setAttribute('aria-pressed', 'false');
          el.setAttribute('tabindex', '0');
        });
        syncHeroIconAriaLabels();
        wrap.removeAttribute('data-active');
        wrap.removeAttribute('data-active-key');
        THEME_VARS_TO_MERGE.forEach(v => wrap.style.removeProperty(v));

        
        setTooltipTextFade(fixedTip, placeholderText);
        wrap.classList.add('has-tooltip-hover');

        const count = icons.length;
        const indexOf = (el) => icons.indexOf(el);
        const wrapIndex = (i, n) => ((i % n) + n) % n; 

        const applyHeroIconState = () => {
          icons.forEach((el) => {
            const isSel = el === selectedIcon;
            el.classList.toggle('selected', isSel);
            el.classList.toggle('visitenkarte-element-is-active', isSel);
            el.classList.toggle('dim', selectedIcon ? !isSel : false);
            el.classList.toggle('selected-dim', false);
            el.setAttribute('role', 'button');
            el.setAttribute('aria-pressed', String(isSel));
            el.setAttribute('tabindex', '0');
            const label = resolveTooltipText(el);
            if (label) el.setAttribute('aria-label', label);
            else el.removeAttribute('aria-label');
          });
        };

        const hideTipHero = () => {
          clearTimeout(wrap._visTipHideTimer);
          clearTimeout(wrap._visTipShowTimer);
          wrap._visTipHideTimer = setTimeout(() => {
            if (selectedIcon) {
              wrap.classList.remove('has-tooltip-hover');
            } else {
              placeholderText = getPlaceholder(); 
              setTooltipTextFade(fixedTip, placeholderText);
              wrap.classList.add('has-tooltip-hover');
              clearDimInCard();
            }
          }, 40);
        };

        const showTipHeroForItem = (item) => {
          const txt = resolveTooltipText(item);
          if (!txt) return;
          clearTimeout(wrap._visTipHideTimer);
          clearTimeout(wrap._visTipShowTimer);
          const delay = Math.max(0, parseInt(item.getAttribute('data-delay') || '0', 10));
          wrap._visTipShowTimer = setTimeout(() => {
            setTooltipTextFade(fixedTip, txt);
            wrap.classList.add('has-tooltip-hover');
          }, delay);
          setDimSiblings(item, true);
        };

        const activateByEl = (icon, { focusIcon = false, manual = false } = {}) => {
          if (!icon) return;
          const nextKey = icon.dataset.key || '';
          if (nextKey && nextKey !== lastActiveKey) {
            wrap.classList.add('hero-theme-fading');
            clearTimeout(heroThemeFadeTimer);
            heroThemeFadeTimer = setTimeout(() => {
              wrap.classList.remove('hero-theme-fading');
            }, 380);
            lastActiveKey = nextKey;
          }
          selectedIcon = icon;

          signalActiveToWrapper(icon);
          applyThemeFromIcon(icon);

          applyHeroIconState();

          setTooltipTextFade(fixedTip, resolveTooltipText(icon) || getPlaceholder());
          wrap.classList.add('has-tooltip-hover');

          if (focusIcon) icon.focus({ preventScroll: true });
          if (manual) scheduleAutoplayRestart(resumeExtra);
        };

        
        icons.forEach((item) => {
          item.addEventListener('mouseenter', () => {
            showTipHeroForItem(item);
            if (selectedIcon && item !== selectedIcon) selectedIcon.classList.add('selected-dim');
            icons.forEach((el) => {
              if (!selectedIcon) {
                el.classList.remove('dim', 'selected-dim');
              } else {
                if (el === item) el.classList.remove('dim');
                else if (el === selectedIcon) el.classList.remove('dim');
                else el.classList.add('dim');
              }
            });
          });

          item.addEventListener('mouseleave', () => {
            selectedIcon?.classList.remove('selected-dim');
            setTooltipTextFade(
              fixedTip,
              selectedIcon ? resolveTooltipText(selectedIcon) : getPlaceholder()
            );
            wrap.classList.add('has-tooltip-hover');
            applyHeroIconState();
          });

          const selectThis = () => activateByEl(item, { manual: true });

          item.addEventListener('click', (e) => {
            e.preventDefault();
            selectThis();
          });

          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectThis();
              return;
            }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              const current = selectedIcon ? indexOf(selectedIcon) : -1;
              const next = icons[wrapIndex(current + 1, count)];
              activateByEl(next, { focusIcon: true, manual: true });
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              const current = selectedIcon ? indexOf(selectedIcon) : 0;
              const prev = icons[wrapIndex(current - 1, count)];
              activateByEl(prev, { focusIcon: true, manual: true });
            } else if (e.key === 'Escape') {
              hideTipHero();
            }
          });

          item.addEventListener('focus', () => showFixedTipForItem(item), true);
          item.addEventListener('blur', hideTipHero, true);
        });

        
        let autoplayStartTimer = null;
        let autoplayRotateTimer = null;

        const clearAutoplayTimers = () => {
          clearTimeout(autoplayStartTimer);
          clearInterval(autoplayRotateTimer);
          autoplayStartTimer = null;
          autoplayRotateTimer = null;
        };

        const startAutoplayRotation = () => {
          clearInterval(autoplayRotateTimer);
          if (!isAreaRuntimeActive()) return;
          autoplayRotateTimer = setInterval(() => {
            if (!isAreaRuntimeActive()) return;
            const current = selectedIcon ? indexOf(selectedIcon) : -1;
            const next = icons[wrapIndex(current + 1, count)];
            activateByEl(next);
          }, rotateInterval);
        };

        const scheduleAutoplayStart = (delayMs) => {
          clearAutoplayTimers();
          if (!isAreaRuntimeActive()) return;
          autoplayStartTimer = setTimeout(() => {
            if (!isAreaRuntimeActive()) return;
            const first = icons[0];
            activateByEl(first);
            startAutoplayRotation();
          }, delayMs);
        };

        const scheduleAutoplayRestart = (extraPauseMs) => {
          clearAutoplayTimers();
          if (!isAreaRuntimeActive()) return;
          autoplayStartTimer = setTimeout(() => {
            if (!isAreaRuntimeActive()) return;
            startAutoplayRotation();
          }, Math.max(0, extraPauseMs));
        };

        
        const heroLangObserver = new MutationObserver((muts) => {
          for (const m of muts) {
            if (m.type === 'attributes' && m.attributeName === 'class') {
              syncHeroIconAriaLabels();
              if (!selectedIcon) {
                setTooltipTextFade(fixedTip, getPlaceholder(), 100);
                wrap.classList.add('has-tooltip-hover');
              } else {
                setTooltipTextFade(fixedTip, resolveTooltipText(selectedIcon), 100);
                wrap.classList.add('has-tooltip-hover');
              }
              break;
            }
          }
        });
        heroLangObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        registerAreaRuntime(
          () => {
            if (selectedIcon) scheduleAutoplayRestart(0);
            else scheduleAutoplayStart(startDelay);
          },
          () => {
            clearAutoplayTimers();
            clearTimeout(heroThemeFadeTimer);
            heroThemeFadeTimer = null;
            wrap.classList.remove('hero-theme-fading');
            selectedIcon?.classList.remove('selected-dim');
            clearDimInCard();
          }
        );

        
        area.addEventListener('hero:activateIndex', (ev) => {
          const idx = Number(ev.detail?.index);
          if (!Number.isNaN(idx)) {
            const icon = icons[wrapIndex(idx, count)];
            activateByEl(icon, { focusIcon: true, manual: true });
          }
        });
      };

      
      const setupDbpSelection = () => {
        const section         = area.closest('.section-dbp') || document;
        const panelContainer  = section.querySelector('.dbp-content-area') || section;
        const panels          = [...panelContainer.querySelectorAll('.dbp-panel')];

        const count = icons.length;
        const wrapIndex1 = (i, n) => ((i - 1 + n) % n) + 1; 

        icons.forEach((el) => el.setAttribute('tabindex', el.getAttribute('tabindex') ?? '0'));

        const swiperEl = section.querySelector('.dbp-swiper');
        let swiper = null;
        let swiperBootToken = 0;
        let swiperInitScheduled = false;

        let selectedIcon  = null;
        let selectedIndex = null;
        let hoverIcon = null;
        let internalSlideSync = false;

        const renderIconState = () => {
          const hasHover = !!hoverIcon;
          icons.forEach((el) => {
            const isSel = el === selectedIcon;
            const isHover = el === hoverIcon;
            el.classList.toggle('selected', isSel);
            el.classList.toggle('visitenkarte-element-is-active', isSel);
            el.classList.toggle('dim', hasHover ? !isSel && !isHover : !isSel);
            el.classList.toggle('selected-dim', hasHover && isSel && !isHover);
            el.setAttribute('aria-selected', String(isSel));
            el.setAttribute('tabindex', isSel ? '0' : '-1');
          });
        };

        const applyPanelState = () => {
          panels.forEach((p) => {
            const idx = Number(p.dataset.dbpPanel || p.getAttribute('data-dbp-panel'));
            const isActive = idx === selectedIndex;
            p.classList.toggle('visitenkarte-element-is-active', isActive);
            p.setAttribute('aria-hidden', String(!isActive));
          });
        };

        const hideTipDbp = () => {
          if (!fixedTip) return;
          clearTimeout(wrap._visTipHideTimer);
          clearTimeout(wrap._visTipShowTimer);
          wrap._visTipHideTimer = setTimeout(() => {
            wrap.classList.remove('has-tooltip-hover');
            renderIconState();
          }, 40);
        };

        const activateByIndex = (idx, { focusIcon = false, fromSwiper = false } = {}) => {
          if (!count) return;
          const safeIdx = wrapIndex1(idx, count);

          let icon = icons.find((i) => Number(i.dataset.dbpIndex) === safeIdx);
          if (!icon) {
            icon = icons.find((i) => {
              const controls = i.getAttribute('aria-controls');
              return controls && controls.endsWith(String(safeIdx));
            });
          }
          if (!icon) return;
          selectedIcon = icon;
          selectedIndex = safeIdx;

          signalActiveToWrapper(icon);
          applyThemeFromIcon(icon);

          renderIconState();
          applyPanelState();
          setTooltipTextFade(fixedTip, resolveTooltipText(icon));
          wrap.classList.add('has-tooltip-hover');

          if (swiper && !fromSwiper) {
            internalSlideSync = true;
            swiper.slideTo(safeIdx - 1, 420, false);
            setTimeout(() => { internalSlideSync = false; }, 0);
          }

          if (focusIcon) selectedIcon.focus({ preventScroll: true });
        };

        
        let initialIcon =
          icons.find((i) => i.getAttribute('aria-selected') === 'true') ||
          icons.find((i) => i.classList.contains('visitenkarte-element-is-active')) ||
          icons[0] || null;

        let initialIndex = 1;
        if (initialIcon) {
          const d = Number(initialIcon.dataset.dbpIndex);
          if (!Number.isNaN(d)) initialIndex = d;
          else {
            const ac = initialIcon.getAttribute('aria-controls');
            const m = ac && ac.match(/(\d+)$/);
            if (m) initialIndex = Number(m[1]);
          }
        }

        const setupDbpSwiper = async () => {
          if (!swiperEl) return;
          const token = ++swiperBootToken;
          try {
            const { Swiper, modules } = await loadSwiperModule('visitenkarten');
            if (token !== swiperBootToken || !swiperEl.isConnected) return;
            const { EffectCreative } = modules;
            const wrapper = swiperEl.querySelector('.swiper-wrapper');
            panels.forEach(p => { p.style.display = 'block'; });
            if (wrapper) {
              const cs = getComputedStyle(wrapper);
              if (cs.display !== 'flex') {
                wrapper.style.display = 'flex';
                wrapper.style.flexWrap = 'nowrap';
              }
            }
            swiperEl.style.overflow = 'visible';

            const markFarSlides = (sw) => {
              const thr = 1;
              sw.slides.forEach((slide) => {
                const prog = typeof slide.progress === 'number' ? slide.progress : 0;
                if (Math.abs(prog) > thr) slide.classList.add('dbp-swiper-far');
                else slide.classList.remove('dbp-swiper-far');
              });
            };

            swiper = new Swiper(swiperEl, {
              modules: EffectCreative ? [EffectCreative] : [],
              direction: 'horizontal',
              loop: false,
              rewind: true,
              slidesPerView: 1,
              spaceBetween: 0,
              speed: 420,
              allowTouchMove: true,
              grabCursor: true,
              autoHeight: true,
              effect: 'creative',
              watchSlidesProgress: true,
              creativeEffect: {
                limitProgress: 1,
                perspective: true,
                prev: { translate: ['-120%', 0, 0], opacity: 0, scale: 1, shadow: false },
                next: { translate: [150, -25, -120], scale: 0.985, opacity: 0.6, shadow: false }
              },
              observer: true,
              observeParents: true,
              on: {
                init() {
                  const startIndex = selectedIndex ?? initialIndex;
                  this.slideTo(Math.max(0, startIndex - 1), 0, false);
                  activateByIndex((this.activeIndex ?? Math.max(0, startIndex - 1)) + 1, { fromSwiper: true });
                  this.updateAutoHeight(0);
                  markFarSlides(this);
                },
                slideChange() {
                  if (internalSlideSync) return;
                  const idx1 = (this.activeIndex ?? 0) + 1;
                  activateByIndex(idx1, { fromSwiper: true });
                },
                progress() { markFarSlides(this); }
              }
            });
          } catch (error) {
            console.warn('[Visitenkarten] Swiper setup failed', error);
          }
        };

        const setupDbpSwiperOnDemand = () => {
          if (!swiperEl || swiper || swiperInitScheduled) return;
          swiperInitScheduled = true;
          setupDbpSwiper().finally(() => {
            swiperInitScheduled = false;
          });
        };

        if (swiperEl) {
          if ('IntersectionObserver' in window) {
            const swiperObserver = new IntersectionObserver((entries) => {
              const shouldInit = entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0);
              if (!shouldInit) return;
              swiperObserver.disconnect();
              setupDbpSwiperOnDemand();
            }, {
              threshold: 0.01,
              rootMargin: '220px 0px'
            });
            swiperObserver.observe(swiperEl);
          } else {
            setupDbpSwiperOnDemand();
          }
        }

        
        activateByIndex(initialIndex);

        
        icons.forEach((item) => {
          item.addEventListener('mouseenter', () => {
            hoverIcon = item;
            setTooltipTextFade(fixedTip, resolveTooltipText(item));
            wrap.classList.add('has-tooltip-hover');
            renderIconState();
          });

          item.addEventListener('mouseleave', () => {
            if (hoverIcon === item) hoverIcon = null;
            if (selectedIcon) {
              setTooltipTextFade(fixedTip, resolveTooltipText(selectedIcon));
              wrap.classList.add('has-tooltip-hover');
            }
            renderIconState();
          });

          const selectThis = () => {
            const idx = Number(item.dataset.dbpIndex);
            if (!Number.isNaN(idx)) {
              activateByIndex(idx);
            }
          };

          item.addEventListener('click', (e) => {
            e.preventDefault();
            selectThis();
          });

          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectThis();
              return;
            }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              activateByIndex((selectedIndex ?? 1) + 1, { focusIcon: true });
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              activateByIndex((selectedIndex ?? 1) - 1, { focusIcon: true });
            } else if (e.key === 'Escape') {
              hideTipDbp();
            }
          });

          item.addEventListener('focus', () => {
            setTooltipTextFade(fixedTip, resolveTooltipText(item));
            wrap.classList.add('has-tooltip-hover');
            renderIconState();
          }, true);
          item.addEventListener('blur', hideTipDbp, true);
        });

        
        const prevBtn = section.querySelector('.dbp-prev');
        const nextBtn = section.querySelector('.dbp-next');
        prevBtn?.addEventListener('click', (e) => { e.preventDefault(); activateByIndex((selectedIndex ?? 1) - 1); });
        nextBtn?.addEventListener('click', (e) => { e.preventDefault(); activateByIndex((selectedIndex ?? 1) + 1); });

        
        area.addEventListener('dbp:activateIndex', (ev) => {
          const idx = Number(ev.detail?.index);
          if (Number.isNaN(idx)) return;
          activateByIndex(idx);
        });
      };

      
      if (area.classList.contains('dbp-visitenkarte-area')) {
        setupDbpSelection();
      } else {
        setupHeroSelection();
      }
    });

    const sectionScope = area.closest('[data-ani-section]');

    if (sectionScope) {
      const syncAreaRuntimeFromSection = () => {
        const active =
          sectionScope.classList.contains('scrolled-active') ||
          sectionScope.classList.contains('is-inview');
        setAreaRuntimeActive(active);
      };
      const sectionStateObserver = new MutationObserver((muts) => {
        for (const m of muts) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            syncAreaRuntimeFromSection();
            break;
          }
        }
      });
      sectionStateObserver.observe(sectionScope, { attributes: true, attributeFilter: ['class'] });
      syncAreaRuntimeFromSection();
    } else {
      setAreaRuntimeActive(true);
    }
  });

  
  function createParticle(layer, x, y) {
    const p = document.createElement('div');
    p.className = 'particle';
    p.style.left = `${x}px`;
    p.style.top  = `${y}px`;
    const size = Math.random() * 8 + 4;
    p.style.width  = `${size}px`;
    p.style.height = `${size}px`;
    p.style.background   = 'rgba(255,255,255,0.6)';
    p.style.position     = 'absolute';
    p.style.borderRadius = '50%';
    p.style.transition   = 'transform 1s ease-out, opacity 1s ease-out';
    layer.appendChild(p);

    requestAnimationFrame(() => {
      const dx = (Math.random() - 0.5) * 50;
      const dy = (Math.random() - 0.5) * 50;
      p.style.transform = `translate(${dx}px, ${dy}px)`;
      p.style.opacity   = '0';
    });
    setTimeout(() => p.remove(), 1000);
  }
}
